import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../state/hooks';
import { useShellProfile } from '../../state/useShellProfile';
import {
  ApplyPage,
  HomePage as PublicHomePage,
  PrivacyPolicy as PublicPrivacyPolicy,
  TermsConditions as PublicTermsConditions,
  type PublicAuthMode,
} from '../../components/public';
import {
  isModuleId,
  isStaffPersona,
  resolvePostAuthTarget,
} from '../routePolicy';
import {
  clearLogoutTransition,
  resolveLogoutTransitionTarget,
} from '../../auth/logoutTransition';

export const PublicRoute = () => {
  const { authStatus, effectivePersona, profile } = useShellProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const shellState = useAppSelector((state) => state.shell);
  const params = new URLSearchParams(location.search);
  const defaultModuleParam = params.get('defaultModule');
  const defaultModule = isModuleId(defaultModuleParam) ? defaultModuleParam : null;
  const logoutTarget = resolveLogoutTransitionTarget();
  const isLogoutLanding = params.get('logout') === '1';
  const holdPublicDuringLogout = Boolean(logoutTarget) || isLogoutLanding;

  const toAuthUrl = (mode: PublicAuthMode, returnTo?: string): string => {
    const nextParams = new URLSearchParams();
    nextParams.set('mode', mode);
    if (defaultModule) {
      nextParams.set('defaultModule', defaultModule);
    }
    if (returnTo) {
      nextParams.set('returnTo', returnTo);
    }
    return `/auth?${nextParams.toString()}`;
  };

  useEffect(() => {
    if (!isLogoutLanding || authStatus === 'authenticated') {
      return;
    }

    clearLogoutTransition();
    navigate('/apply', { replace: true });
  }, [authStatus, isLogoutLanding, navigate]);

  if (authStatus === 'authenticated' && !holdPublicDuringLogout) {
    const target = resolvePostAuthTarget({
      persona: effectivePersona,
      lastVisitedPath: shellState.lastVisitedPath,
      defaultModule: defaultModule ?? shellState.defaultModule,
      authGroups: profile?.groups,
    });
    return <Navigate to={target} replace />;
  }

  return (
    <PublicHomePage
      onSignIn={() => navigate(toAuthUrl('signin', '/apply'))}
      onSignUp={() => navigate(toAuthUrl('signup', '/apply'))}
      onNavigatePrivacy={() => navigate('/privacy-policy')}
      onNavigateTerms={() => navigate('/terms-conditions')}
    />
  );
};

export const PrivacyPolicyRoute = () => {
  const navigate = useNavigate();
  return (
    <PublicPrivacyPolicy
      onBack={() => navigate('/apply')}
      onNavigatePrivacy={() => navigate('/privacy-policy')}
      onNavigateTerms={() => navigate('/terms-conditions')}
    />
  );
};

export const TermsConditionsRoute = () => {
  const navigate = useNavigate();
  return (
    <PublicTermsConditions
      onBack={() => navigate('/apply')}
      onNavigatePrivacy={() => navigate('/privacy-policy')}
      onNavigateTerms={() => navigate('/terms-conditions')}
    />
  );
};

export const ApplyRoute = () => {
  const { effectivePersona } = useShellProfile();
  if (isStaffPersona(effectivePersona)) {
    return <Navigate to="/school/admissions/dashboard" replace />;
  }
  return <ApplyPage />;
};
